import {
  ExtranetOrderTicketGroupDto,
  GetExtranetOrderTicketGroupQueryDto,
} from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetTabs,
  useBreakpoint,
  useIntersectionObserver,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacH4, PharmacIcon, PharmacText, PharmacTooltip } from '@pharmac/ui'
import { UseQueryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { ExtranetFilters } from '../../components/filters/ExtranetFilters'
import { OrderTicketFilters } from '../../components/order-tickets/OrderTicketFilters'
import { OrderTicketsTable } from '../../components/order-tickets/OrderTicketsTable'
import { getOrderTicketsQueryKeyFactory } from '../../components/order-tickets/util/order-ticket-query-key-factory'
import { mapOrderTicketParamsToQueryParams } from '../../components/order-tickets/util/query-parameter-mapping'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { cleanQueryParams } from '../../features/infinite-query/util/cleanQueryParams'
import { getPages } from '../../features/infinite-query/util/getPages'
import { api } from '../../services/HttpService'

export const OrderTicketsView = () => {
  const { t } = useLanguage()
  const { minDesktop, maxMobile } = useBreakpoint()
  const { customer } = useCustomerContext()

  const [searchParams, setSearchParams] = useSearchParams()

  const [params, setParams] = useState<GetExtranetOrderTicketGroupQueryDto>(
    mapOrderTicketParamsToQueryParams(searchParams)
  )

  const [isActiveTickets, setIsActiveTickets] = useState(true)

  const getAllActiveOrderTicketGroups = (
    customerId?: number
  ): UseQueryOptions<Array<ExtranetOrderTicketGroupDto>> => ({
    queryFn: async () => await api.orders.getAllActiveOrderTicketGroups(customerId as number),
    queryKey: getOrderTicketsQueryKeyFactory.active(customer?.id),
    enabled: typeof customerId === 'number',
  })

  const { data: activeTicketGroups } = useQuery(getAllActiveOrderTicketGroups(customer?.id))

  const {
    data: completedOrderTicketGroups,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      ...getOrderTicketsQueryKeyFactory.completed(customer?.id),
      params.startDeadlineRange,
      params.endDeadlineRange,
      params.wholeName,
    ],
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  })

  const fetchPage = (pageParam: number) => {
    return api.orders.getCompletedOrderTicketGroups(customer!.id!, {
      pageSize: 20,
      ...params,
      page: pageParam,
    })
  }
  const completedTicketGroups = getPages(completedOrderTicketGroups)

  const handleReset = () => {
    setSearchParams(createSearchParams({}))
  }

  const hasActiveFilters = () => {
    const params = mapOrderTicketParamsToQueryParams(searchParams)

    const hasNoSearchParams = Object.keys(params).length === 0

    return !hasNoSearchParams
  }

  const activeFilterCount = () => {
    if (Object.keys(params).includes('endDeadlineRange')) return Object.keys(params).length - 1
    return Object.keys(params).length
  }

  useEffect(() => {
    setParams(() => mapOrderTicketParamsToQueryParams(searchParams))
  }, [searchParams])

  useEffect(() => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...params,
        })
      )
    )
  }, [])

  const [scrollRef] = useIntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      fetchNextPage()
    }
  })

  const noSearchResults = !isLoading && !isFetchingNextPage && Object.keys(params).length !== 0

  return (
    <ExtranetWrapper>
      <ExtranetContainer
        full
        paddingMediumBottom
        paddingSmallHorizontal={!minDesktop}
      >
        <ExtranetContainer
          full
          alignCenter={minDesktop}
          paddingLargeVertical={minDesktop}
          paddingLargeTop={!minDesktop}
          paddingMediumBottom={!minDesktop}
        >
          <PharmacH4
            style={{ color: 'var(--color-primary)' }}
            paddingMediumRight
          >
            {t('extranet:menu.omaAnnosOrderTickets')}
          </PharmacH4>
          <PharmacTooltip
            maxWidth={maxMobile ? '295px' : '500px'}
            handle={
              <PharmacIcon
                style={{ color: 'var(--color-info)' }}
                size="24px"
                icon="info"
              />
            }
          >
            <PharmacText>{t('extranet:correctionRequests.titleTooltip')}</PharmacText>
          </PharmacTooltip>
          {!minDesktop && !isActiveTickets && (
            <>
              <div style={{ marginLeft: 'var(--spacing-medium)' }} />
              <ExtranetFilters
                activeFilterCount={activeFilterCount()}
                title={t('filters', { ns: 'common' })}
                resetFilters={handleReset}
                hasActiveFilters={hasActiveFilters()}
                render={({ closeFilters }) => <OrderTicketFilters closeFilters={closeFilters} />}
              />
            </>
          )}
        </ExtranetContainer>
        {minDesktop && !isActiveTickets && (
          <ExtranetContainer
            half
            justifyEnd
            paddingLargeVertical={minDesktop}
          >
            <ExtranetFilters
              activeFilterCount={activeFilterCount()}
              title={t('filters', { ns: 'common' })}
              resetFilters={handleReset}
              hasActiveFilters={hasActiveFilters()}
              render={({ closeFilters }) => <OrderTicketFilters closeFilters={closeFilters} />}
            />
          </ExtranetContainer>
        )}
      </ExtranetContainer>
      <ExtranetTabs
        selectedIndex={0}
        paddingMedium
        tabs={[
          {
            index: 0,
            handle: t('extranet:correctionRequests.activeRequests'),
            element: (
              <OrderTicketsTable
                key="activeTickets"
                groupData={activeTicketGroups}
              />
            ),
            onClick: () => setIsActiveTickets(true),
          },
          {
            index: 1,
            handle: t('extranet:correctionRequests.checkedRequests'),
            element: (
              <OrderTicketsTable
                key="completedTickets"
                groupData={completedTicketGroups}
                requestChecked
                noSearchResults={noSearchResults}
                scrollRef={scrollRef}
                isLoading={isLoading}
              />
            ),
            onClick: () => setIsActiveTickets(false),
          },
        ]}
      />
    </ExtranetWrapper>
  )
}
